import { Preferences } from '@capacitor/preferences'
import { TRPCClientError } from '@trpc/client'

import authClient from './authClient'

// --- Storage Helpers ---
export const setAccessToken = (accessToken: string) => {
	return Preferences.set({ key: 'accessToken', value: accessToken })
}
export const setRefreshToken = (refreshToken: string) => {
	return Preferences.set({ key: 'refreshToken', value: refreshToken })
}

export const clearTokens = async () => {
	return Promise.all([
		Preferences.remove({ key: 'accessToken' }),
		Preferences.remove({ key: 'refreshToken' }),
	])
}

// --- Token Getters ---
export const getCustomAccessToken = async (): Promise<string | null> => {
	const { value } = await Preferences.get({ key: 'accessToken' })
	return value
}

type RefreshResult =
	| { ok: true; accessToken: string; refreshToken: string }
	| { ok: false; isAuthFailure: boolean }

let refreshTokenPromise: Promise<RefreshResult> | null = null

export const refreshCustomAccessToken = async (): Promise<RefreshResult> => {
	if (refreshTokenPromise) {
		return refreshTokenPromise
	}

	refreshTokenPromise = (async (): Promise<RefreshResult> => {
		const { value: refreshToken } = await Preferences.get({ key: 'refreshToken' })

		if (!refreshToken) {
			return { ok: false, isAuthFailure: true }
		}

		try {
			const data = await authClient.auth.refresh.mutate({ refreshToken })
			await Promise.all([setAccessToken(data.accessToken), setRefreshToken(data.refreshToken)])

			return { ok: true, ...data }
		} catch (error) {
			if (error instanceof TRPCClientError && error.data?.code === 'UNAUTHORIZED') {
				await clearTokens()

				return { ok: false, isAuthFailure: true }
			}
			// Network/server error — tokens may still be valid, don't clear them
			return { ok: false, isAuthFailure: false }
		}
	})()

	return refreshTokenPromise.finally(() => {
		refreshTokenPromise = null
	})
}
